import type {
  BoardFeatureRow,
  DeliveryStatus,
  MilestoneRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

/**
 * View-model for the live implementation gantt (BF-178).
 *
 * Each workstream carries an explicit `start_date` / `end_date` (BF-196) and its
 * bar is placed directly on the calendar axis from those — a staggered, overlapping
 * schedule that mirrors the hand-drawn roadmap gantt in the workplan deck (rather
 * than the old milestone-band derivation, which collapsed same-milestone streams
 * into identical bars). A workstream missing either date is listed as unscheduled.
 * The axis spans the union of milestone dates + workstream dates.
 *
 * Completion is a SEGMENTED status mix, not a single done/total scalar (BF-380): a
 * lane carries one `segments` entry per actionable status (done / in-review /
 * in-progress / blocked), each holding its own share of the lane, so the bar renders
 * as a stacked fill. `deferred` features are excluded from the denominator — a parked
 * item is out of scope, and counting it would permanently cap the lane below 100%.
 * `completionPct` is retained as the done-only headline number. Pure projection —
 * unit-tested.
 */

export interface GanttMonthTick {
  label: string;
  pct: number;
}

export interface GanttAxis {
  start: string;
  end: string;
  monthTicks: GanttMonthTick[];
}

/**
 * The statuses that earn a coloured segment in a lane bar, in left-to-right fill
 * order: shipped work first, then work in flight, with blocked at the frontier so
 * the bar reads "done → moving → stuck". Statuses absent from this list (not-started,
 * deferred) draw no segment — not-started is the empty remainder of the track.
 */
export const LANE_FILL_ORDER = ["done", "in-review", "in-progress", "blocked"] as const;

export type LaneFillStatus = (typeof LANE_FILL_ORDER)[number];

export interface GanttLaneSegment {
  status: LaneFillStatus;
  count: number;
  /**
   * Share of the lane's scoped total, 0–100. Deliberately UNROUNDED: rounding each
   * segment independently lets four of them sum to 101–102% and overflow the bar,
   * so exact fractions go to CSS and only display text is rounded.
   */
  pct: number;
}

export interface GanttLane {
  key: string;
  code: string;
  title: string;
  color: string;
  startPct: number;
  widthPct: number;
  completionPct: number;
  doneCount: number;
  // Denominator behind completionPct and every segment pct: all features on the
  // workstream EXCEPT deferred ones.
  totalCount: number;
  // One entry per actionable status present on the lane, in LANE_FILL_ORDER.
  // Zero-count statuses are omitted, so an all-done lane has a single segment.
  segments: GanttLaneSegment[];
  // Raw ISO dates the bar was placed from (always present on a lane — a workstream
  // missing either date is `unscheduled`, not a lane). Carried so the inline editor
  // (BF-198) can pre-fill without a second fetch.
  startDate: string;
  endDate: string;
  // Scope + effort, surfaced on hover (BF-213). Nullable — a workstream may have no
  // summary or effort estimate seeded.
  summary: string | null;
  effortMin: number | null;
  effortMax: number | null;
}

export interface GanttMilestoneMarker {
  key: string;
  code: string;
  title: string;
  datePct: number;
  // Raw ISO target date the diamond is placed from — carried so the inline editor
  // can pre-fill without a second fetch (a marker is only produced for a dated
  // milestone, so this is always present).
  date: string;
  isGate: boolean;
  reached: boolean;
  // "Why this milestone" reasoning (from the workplan deck), revealed on click.
  description: string | null;
}

export interface UnscheduledLane {
  key: string;
  code: string;
  title: string;
}

export interface ImplementationGanttViewModel {
  axis: GanttAxis | null;
  lanes: GanttLane[];
  milestones: GanttMilestoneMarker[];
  unscheduled: UnscheduledLane[];
  nowPct: number | null;
}

/**
 * Stable per-workstream colour. Build blocks get the brand accent, a muted
 * secondary tone alternates for readability, deploy/meta-ish rows are grey, and
 * a validation-style row (code starting "V") is amber. Keyed off the workstream
 * code's leading token so it stays consistent across reorders.
 */
const COLOR_ACCENT = "var(--primary)";
const COLOR_SECONDARY = "oklch(0.72 0.10 208)";
const COLOR_MUTED = "var(--muted-foreground)";
const COLOR_VALIDATION = "var(--rag-amber)";
const COLOR_CROSS = "oklch(0.60 0.025 250)";

export function laneColor(workstream: Pick<WorkstreamRow, "code" | "sort_order">): string {
  const head = workstream.code.trim().charAt(0).toUpperCase();
  if (head === "V") return COLOR_VALIDATION;
  if (head === "X") return COLOR_CROSS;
  if (head === "0") return COLOR_MUTED;
  // Alternate accent / secondary for build blocks so adjacent lanes read apart.
  return workstream.sort_order % 2 === 0 ? COLOR_ACCENT : COLOR_SECONDARY;
}

type StatusCounts = Record<DeliveryStatus, number>;

function zeroCounts(): StatusCounts {
  return {
    "not-started": 0,
    "in-progress": 0,
    blocked: 0,
    "in-review": 0,
    done: 0,
    deferred: 0,
  };
}

/**
 * The denominator for a lane: every feature on the workstream except the deferred
 * ones. Deferred work is parked out of scope, so including it would hold the lane
 * permanently short of 100% with nothing actionable left to do.
 */
function scopedCount(counts: StatusCounts): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0) - counts.deferred;
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function dateMs(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getTime();
}

/**
 * `today` reduced to its UTC date at 00:00, so it compares like the milestone
 * dates (which are anchored at 00:00:00Z). Without this, any time-of-day on the
 * final milestone's date makes today > axisEnd and the now-line vanishes instead
 * of sitting at 100%.
 */
function dateOnlyMs(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Month ticks (1st of each month) spanning [start, end] inclusive of edges. */
function buildMonthTicks(startMs: number, endMs: number): GanttMonthTick[] {
  const span = endMs - startMs;
  if (span <= 0) return [];
  const ticks: GanttMonthTick[] = [];
  const cursor = new Date(startMs);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  // First tick is the month containing axisStart, anchored at the start edge.
  while (cursor.getTime() <= endMs) {
    const ms = cursor.getTime();
    const pct = clamp01((ms - startMs) / span) * 100;
    ticks.push({
      label: cursor.toLocaleString("en-US", { month: "short", timeZone: "UTC" }),
      pct,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

export function buildImplementationGantt(
  workstreams: WorkstreamRow[],
  milestones: MilestoneRow[],
  features: Pick<BoardFeatureRow, "workstream_key" | "delivery_status">[],
  today: Date
): ImplementationGanttViewModel {
  const orderedMilestones = [...milestones].sort((a, b) => a.sort_order - b.sort_order);
  const datedMilestones = orderedMilestones.filter(
    (m): m is MilestoneRow & { target_date: string } => m.target_date !== null
  );

  // Anchor dates = dated milestones + FULLY-scheduled workstream dates. The axis
  // spans their union so both the lane bars and the milestone diamonds fit even
  // when a workstream's schedule runs past the last milestone. Only workstreams
  // with BOTH endpoints contribute — a partial (one-date) workstream is treated as
  // unscheduled by the lane loop below, so it must not stretch the axis either.
  // Falsy (not just === null) guards against a row whose start_date/end_date is
  // `undefined` — e.g. queried from a DB before migration 0120 added the columns —
  // which would otherwise flow into dateMs() as NaN and break the whole axis.
  const wsDateStrings = workstreams.flatMap((ws) =>
    ws.start_date && ws.end_date ? [ws.start_date, ws.end_date] : []
  );
  const anchorDates = [...datedMilestones.map((m) => m.target_date), ...wsDateStrings].sort();

  if (anchorDates.length === 0) {
    return { axis: null, lanes: [], milestones: [], unscheduled: [], nowPct: null };
  }

  const axisStartIso = anchorDates[0]!;
  const axisEndIso = anchorDates[anchorDates.length - 1]!;
  const startMs = dateMs(axisStartIso);
  const endMs = dateMs(axisEndIso);
  const span = endMs - startMs;

  const pct = (ms: number): number => (span <= 0 ? 0 : clamp01((ms - startMs) / span) * 100);

  // Status mix per workstream key — every status counted, so the lane bar can
  // segment by status rather than collapsing to a done/not-done pair.
  const totals = new Map<string, StatusCounts>();
  for (const f of features) {
    const bucket = totals.get(f.workstream_key) ?? zeroCounts();
    bucket[f.delivery_status] += 1;
    totals.set(f.workstream_key, bucket);
  }

  // Lanes are ordered by their start date on the calendar (BF-213), so the column
  // reads top-to-bottom in the order work begins. Undated (unscheduled) workstreams
  // sort last (they fall out into the unscheduled list); ties break on sort_order.
  const orderedWorkstreams = [...workstreams].sort((a, b) => {
    if (a.start_date && b.start_date) {
      if (a.start_date !== b.start_date) return a.start_date < b.start_date ? -1 : 1;
      return a.sort_order - b.sort_order;
    }
    if (a.start_date) return -1;
    if (b.start_date) return 1;
    return a.sort_order - b.sort_order;
  });

  const lanes: GanttLane[] = [];
  const unscheduled: UnscheduledLane[] = [];

  for (const ws of orderedWorkstreams) {
    // A lane is placed by its own start/end dates. A workstream missing either
    // endpoint (null, or undefined pre-0120) has no hand-set schedule → listed as
    // unscheduled, never guessed (falsy guard so undefined can't reach dateMs()).
    if (!ws.start_date || !ws.end_date) {
      unscheduled.push({ key: ws.key, code: ws.code, title: ws.title });
      continue;
    }
    const startPct = pct(dateMs(ws.start_date));
    const endPct = pct(dateMs(ws.end_date));
    const widthPct = Math.max(endPct - startPct, 0);

    const counts = totals.get(ws.key) ?? zeroCounts();
    const scopedTotal = scopedCount(counts);
    const segments: GanttLaneSegment[] = LANE_FILL_ORDER.map((status) => ({
      status,
      count: counts[status],
      pct: scopedTotal === 0 ? 0 : (counts[status] / scopedTotal) * 100,
    })).filter((seg) => seg.count > 0);
    const completionPct = scopedTotal === 0 ? 0 : Math.round((counts.done / scopedTotal) * 100);

    lanes.push({
      key: ws.key,
      code: ws.code,
      title: ws.title,
      color: laneColor(ws),
      startPct,
      widthPct,
      completionPct,
      doneCount: counts.done,
      totalCount: scopedTotal,
      segments,
      startDate: ws.start_date,
      endDate: ws.end_date,
      summary: ws.summary,
      effortMin: ws.effort_min_pw,
      effortMax: ws.effort_max_pw,
    });
  }

  // Which workstreams target each milestone (for the "reached" heuristic).
  const wsByMilestone = new Map<string, WorkstreamRow[]>();
  for (const ws of orderedWorkstreams) {
    if (ws.target_milestone_key === null) continue;
    const list = wsByMilestone.get(ws.target_milestone_key) ?? [];
    list.push(ws);
    wsByMilestone.set(ws.target_milestone_key, list);
  }

  const todayMs = dateOnlyMs(today);

  const milestoneMarkers: GanttMilestoneMarker[] = datedMilestones.map((m) => {
    const inPast = dateMs(m.target_date) <= todayMs;
    const targeting = wsByMilestone.get(m.key) ?? [];
    const allTargetingDone =
      targeting.length > 0 &&
      targeting.every((ws) => {
        const counts = totals.get(ws.key);
        if (counts === undefined) return false;
        const scoped = scopedCount(counts);
        return scoped > 0 && counts.done === scoped;
      });
    return {
      key: m.key,
      code: m.code,
      title: m.title,
      datePct: pct(dateMs(m.target_date)),
      date: m.target_date,
      isGate: m.is_gate,
      reached: inPast || allTargetingDone,
      description: m.description,
    };
  });

  const nowPct = todayMs < startMs || todayMs > endMs ? null : pct(todayMs);

  return {
    axis: {
      start: axisStartIso,
      end: axisEndIso,
      monthTicks: buildMonthTicks(startMs, endMs),
    },
    lanes,
    milestones: milestoneMarkers,
    unscheduled,
    nowPct,
  };
}
